import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Modal,
  TextField,
  InputAdornment,
  Card,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Paginations from "../../common/pagination/Paginations";
import { allOrderListApi, OrderDetailsApi } from "../../api";
import { useSelector } from "react-redux";
import UserImg from "../../common/assests/sampleuser.svg";
import ReyLeaf from "../../common/assests/reyleafLogo.svg";
import { AdminTable, ActionCell } from "../../common/adminTable";
import configuration from "../../config/Config";

const AllOrder = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const { id } = useParams();
  const [allOrderData, setAllOrderData] = useState([]);
  const [orderProducts, setOrderProducts] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [myTimout, setMyTimeOut] = useState();
  const [open, setOpen] = useState(false);

  const handleAllOrderList = async () => {
    try {
      setLoading(true);
      const response = await allOrderListApi({ token, currentPage, id, searchQuery });
      setAllOrderData(response?.orders || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleAllOrderList();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      handleAllOrderList();
    } else {
      clearTimeout(myTimout);
      const timeout = setTimeout(() => handleAllOrderList(), 500);
      setMyTimeOut(timeout);
    }
  }, [searchQuery]);

  const handleOrderDetail = async (orderId) => {
    try {
      const response = await OrderDetailsApi({ token, id: orderId });
      setOrderProducts(response || null);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleClickOpen = (row) => {
    handleOrderDetail(row?._id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const columns = [
    {
      field: "seller",
      headerName: "Seller Name",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={row?.seller?.picture ? `${configuration.cdnFront}/${row.seller.picture}` : ReyLeaf}
            alt="seller"
            style={{ height: 32, width: 32, borderRadius: "50%", flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row?.seller?.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      minWidth: 140,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (row) =>
        row?.address ? [row.address.address || ""].filter(Boolean).join(", ") : "-",
    },
    {
      field: "totalAmount",
      headerName: "Total Price",
      width: 110,
      renderCell: (row) => `$${row?.totalAmount ?? "-"}`,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 90,
      renderCell: (row) =>
        row?.items?.reduce((total, item) => total + (item?.quantity || 0), 0),
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 130,
      renderCell: (row) => row?.paymentStatus || "-",
    },
    {
      field: "status",
      headerName: "Order Status",
      width: 110,
      renderCell: (row) => row?.status || "-",
    },
    {
      field: "__actions",
      headerName: "",
      width: 60,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
          ]}
        />
      ),
    },
  ];

  const productColumns = [
    {
      field: "photo",
      headerName: "Image",
      width: 70,
      renderCell: (row) => (
        <img
          src={row?.product?.photos?.length > 0 ? `${configuration.cdnFront}/${row.product.photos[0].url}` : ReyLeaf}
          alt="product"
          style={{ height: 40, width: 40, borderRadius: "6px", objectFit: "cover" }}
        />
      ),
    },
    { field: "title", headerName: "Product Name", minWidth: 140, renderCell: (r) => r?.product?.title || "-" },
    { field: "category", headerName: "Category", width: 120, renderCell: (r) => r?.product?.categoryId?.name || "-" },
    { field: "qty", headerName: "Quantity", width: 80, renderCell: (r) => r?.quantity || "-" },
    { field: "price", headerName: "Regular Price", width: 110, renderCell: (r) => `$${r?.product?.price || "-"}` },
    {
      field: "itemTotal",
      headerName: "Item Total",
      width: 100,
      renderCell: (r) => `$${(r?.quantity * (r?.product?.discountedProce || 0)).toFixed(2)}`,
    },
    { field: "discounted", headerName: "Discounted Price", width: 130, renderCell: (r) => `$${r?.product?.discountedProce || "-"}` },
  ];

  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "40px", marginBottom: "30px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <Typography sx={{ fontSize: { xs: "16px", sm: "20px" }, fontWeight: "600", color: "#362B24" }}>
            Order List
          </Typography>
          <TextField
            placeholder="Search by order status"
            value={searchQuery}
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setSearchQuery(e.target.value);
              }
            }}
            size="small"
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#fff",
                fontFamily: "Heebo",
                "& fieldset": { borderColor: "#E7E7E7" },
                "&:hover fieldset": { borderColor: "#D7D7D7" },
                "&.Mui-focused fieldset": { borderColor: "#065F46" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#8B8B8B", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <AdminTable
          columns={columns}
          rows={allOrderData}
          loading={loading}
          rowKey="_id"
          emptyText="No orders found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            maxHeight: "80vh",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "750px",
            width: "90%",
            bgcolor: "#F5F5F5",
            borderRadius: "32px",
            boxShadow: 24,
            overflowY: "auto",
            p: 4,
            "&::-webkit-scrollbar": { display: "none" },
            "@media (max-width: 1400px)": { width: "80vw" },
            "@media (max-width: 1200px)": { width: "60vw" },
            "@media (max-width: 1024px)": { width: "60vw" },
            "@media (max-width: 767px)": { width: "80vw" },
            "@media (max-width: 576px)": { width: "90vw" },
            "@media (max-width: 480px)": { width: "95vw" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Typography variant="h6" sx={{ marginBottom: "16px", color: "#362B24", fontSize: "20px", fontWeight: "700" }}>
              Order Details
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {orderProducts ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                <Box>
                  <Typography sx={{ fontSize: "15px", fontWeight: "500", color: "#362B24" }}>Order By</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginTop: "15px" }}>
                    <img
                      src={orderProducts?.user?.picture ? `${configuration.cdnFront}/${orderProducts.user.picture}` : UserImg}
                      alt="user"
                      style={{ height: "42px", width: "42px", borderRadius: "25px" }}
                    />
                    <strong>{orderProducts?.user?.fullname || "-"}</strong>
                  </Box>
                </Box>
              </Box>
              <br />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(142px, 1fr))", gap: 3 }}>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Order Date</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>
                    <strong>
                      {orderProducts?.createdAt
                        ? new Date(orderProducts.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                        : "-"}
                    </strong>
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Order ID</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{orderProducts?.orderId || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Order Status</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{orderProducts?.status || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24", fontSize: "15px" }}>Payment Status</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{orderProducts?.paymentStatus || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Card</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{orderProducts?.card || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Total Price</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{`$${orderProducts?.totalAmount || "-"}`}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Shipping Address</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>
                    <strong>
                      {orderProducts?.address
                        ? [
                            orderProducts.address.address || "",
                            orderProducts.address.city || "",
                            orderProducts.address.state || "",
                            orderProducts.address.country || "",
                            orderProducts.address.zipcode || "",
                          ].filter(Boolean).join(", ")
                        : "-"}
                    </strong>
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Seller Name</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginTop: "8px" }}>
                    <img
                      src={orderProducts?.seller?.picture ? `${configuration.cdnFront}/${orderProducts.seller.picture}` : UserImg}
                      alt="seller"
                      style={{ width: "42px", height: "42px", borderRadius: "25px" }}
                    />
                    <strong>{orderProducts?.seller?.fullname || "-"}</strong>
                  </Box>
                </Box>
              </Box>

              <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "40px" }}>
                <AdminTable
                  columns={productColumns}
                  rows={orderProducts?.items || []}
                  rowKey="_id"
                  emptyText="No items"
                />
              </Card>
            </Box>
          ) : (
            <Typography>Order Not Found</Typography>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default AllOrder;
